import React, { useState } from "react";
import {
  SearchIcon,
  CalendarIcon,
  XIcon,
  DollarSignIcon,
  BriefcaseIcon,
  MapPinIcon,
  TagIcon,
} from "lucide-react";
import { listCurrency } from "@constant/app";

interface FilterModalProps {
  onClose: () => void;
  onApply: (filters: Record<string, any>) => void;
}

export function FilterModal({ onClose, onApply }: FilterModalProps) {
  const [postedDate, setPostedDate] = useState("2024-04-20");
  const [minSalary, setMinSalary] = useState("0");
  const [maxSalary, setMaxSalary] = useState("200000");
  const [jobType, setJobType] = useState("All Types");
  const [location, setLocation] = useState("All Locations");
  const [currency, setCurrency] = useState("USD");
  const [department, setDepartment] = useState("All Departments");
  const [status, setStatus] = useState("All Statuses");
  const [jobTitle, setJobTitle] = useState("");
  const [tags, setTags] = useState("");

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <SearchIcon
                className="h-5 w-5 text-blue-600 mr-2"
              />

              <h2 className="text-xl font-semibold text-gray-900">
                Filter Job Listings
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <XIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="space-y-4">
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Job Title or Keywords
                  </label>
                  <input
                    type="text"
                    placeholder="Frontend Developer, Data Scientist, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Department
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                  >
                    <option value="All Departments">
                      All Departments
                    </option>
                    <option value="Engineering">
                      Engineering
                    </option>
                    <option value="Design">
                      Design
                    </option>
                    <option value="Product">
                      Product
                    </option>
                    <option value="Marketing">
                      Marketing
                    </option>
                    <option value="Sales">
                      Sales
                    </option>
                    <option value="Finance">
                      Finance
                    </option>
                    <option value="HR">
                      HR
                    </option>
                    <option value="Data">
                      Data
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Location
                  </label>
                  <div className="relative">
                    <MapPinIcon
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                    />

                    <input
                      type="text"
                      placeholder="Remote, San Francisco, New York, etc."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-gray-900"
                      value={location !== "All Locations" ? location : ""}
                      onChange={(e) =>
                        setLocation(e.target.value || "All Locations")
                      }
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Job Status
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="All Statuses">
                      All Statuses
                    </option>
                    <option value="Active">
                      Active
                    </option>
                    <option value="Paused">
                      Paused
                    </option>
                    <option value="Closed">
                      Closed
                    </option>
                    <option value="Draft">
                      Draft
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg">
              <div className="space-y-4">
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Job Type
                  </label>
                  <div className="relative">
                    <BriefcaseIcon
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                    />

                    <select
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-gray-900"
                      value={jobType}
                      onChange={(e) => setJobType(e.target.value)}
                    >
                      <option value="All Types">
                        All Types
                      </option>
                      <option value="Full-time">
                        Full-time
                      </option>
                      <option value="Part-time">
                        Part-time
                      </option>
                      <option value="Contract">
                        Contract
                      </option>
                      <option value="Internship">
                        Internship
                      </option>
                      <option value="Temporary">
                        Temporary
                      </option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Tags
                  </label>
                  <div className="relative">
                    <TagIcon
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                    />

                    <input
                      type="text"
                      placeholder="Remote, Urgent, Featured, etc."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-gray-900"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Posted Date (From)
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={postedDate}
                      onChange={(e) => setPostedDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                    />

                    <CalendarIcon
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Min Salary
                    </label>
                    <div className="flex">
                      <div className="relative flex-1">
                        <DollarSignIcon
                          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                        />

                        <input
                          type="number"
                          placeholder="0"
                          value={minSalary}
                          onChange={(e) => setMinSalary(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-l-md text-gray-900"
                        />
                      </div>
                      <select
                        className="px-2 py-2 border border-l-0 border-gray-300 rounded-r-md text-gray-900 bg-gray-50"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                      >
                        {listCurrency.map((currency, index) => (
                          <option key={index} value={currency}>{currency}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Max Salary
                    </label>
                    <div className="flex">
                      <div className="relative flex-1">
                        <DollarSignIcon
                          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                        />

                        <input
                          type="number"
                          placeholder="200000"
                          value={maxSalary}
                          onChange={(e) => setMaxSalary(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-l-md text-gray-900"
                        />
                      </div>
                      <select
                        className="px-2 py-2 border border-l-0 border-gray-300 rounded-r-md text-gray-900 bg-gray-50"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                      >
                        {listCurrency.map((currency, index) => (
                          <option key={index} value={currency}>{currency}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-5">
            <button
              onClick={onClose}
              className="px-4 py-2 text-blue-600 font-medium text-sm rounded-md hover:bg-blue-50"
            >
              Clear filters
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-white font-medium text-sm rounded-md bg-blue-600 hover:bg-blue-700"
            >
              Apply filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
